import { gql, useLazyQuery } from '@apollo/client';
import Select from '@orn/shared-web/components/Select';
import * as React from 'react';

import type {
  AdminListRecurringTransactionsQuery,
  AdminListRecurringTransactionsQueryVariables,
} from './__generated__/AdminSelectRecurringTransaction.gql';

const ADMIN_LIST_RECURRING_TRANSACTIONS = gql`
  query AdminListRecurringTransactions($familyId: ID!) {
    adminListRecurringTransactions(familyId: $familyId) {
      id
      description
    }
  }
`;

interface IAdminSelectRecurringTransactionProps {
  selectedFamilyId: string | null;
  selectedRecurringTransactionId: string | null;
  onSelectionChange: (recurringTransactionId: string | null) => void;
  label?: string;
  className?: string;
}

/**
 * Admin recurring-transaction picker. Loads recurring transactions for the
 * selected family via the mops-api `adminListRecurringTransactions` query and
 * renders them in a `Select`.
 */
const AdminSelectRecurringTransaction = ({
  selectedFamilyId,
  selectedRecurringTransactionId,
  onSelectionChange,
  label = 'Recurring transaction',
  className,
}: IAdminSelectRecurringTransactionProps) => {
  const [loadRecurringTransactions, { data: recurringTransactionsData, loading: isLoading }] = useLazyQuery<
    AdminListRecurringTransactionsQuery,
    AdminListRecurringTransactionsQueryVariables
  >(ADMIN_LIST_RECURRING_TRANSACTIONS);
  const previousSelectedFamilyIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!selectedFamilyId) {
      previousSelectedFamilyIdRef.current = null;
      onSelectionChange(null);
      return;
    }

    if (previousSelectedFamilyIdRef.current != null && previousSelectedFamilyIdRef.current !== selectedFamilyId) {
      onSelectionChange(null);
    }

    previousSelectedFamilyIdRef.current = selectedFamilyId;
    void loadRecurringTransactions({ variables: { familyId: selectedFamilyId } });
  }, [selectedFamilyId, loadRecurringTransactions, onSelectionChange]);

  const recurringTransactions = React.useMemo(() => {
    if (!selectedFamilyId) {
      return [];
    }

    const recurringTransactionItems =
      recurringTransactionsData?.adminListRecurringTransactions.map((recurringTransaction) => ({
        id: recurringTransaction.id,
        label: recurringTransaction.description || `Recurring transaction ${recurringTransaction.id}`,
      })) ?? [];

    if (recurringTransactionItems.length === 0) {
      return [{ id: 'no-recurring-transactions-found', label: 'No items found', isDisabled: true }];
    }

    return recurringTransactionItems;
  }, [selectedFamilyId, recurringTransactionsData?.adminListRecurringTransactions]);

  return (
    <Select
      label={label}
      placeholder={selectedFamilyId ? 'Select a recurring transaction...' : 'Select a user first'}
      value={selectedRecurringTransactionId || null}
      onChange={(key) => {
        const nextRecurringTransactionId = key ? (key as string) : null;
        if (!nextRecurringTransactionId || nextRecurringTransactionId === 'no-recurring-transactions-found') {
          onSelectionChange(null);
          return;
        }
        onSelectionChange(nextRecurringTransactionId);
      }}
      isDisabled={!selectedFamilyId || isLoading}
      items={recurringTransactions}
      className={className}
    >
      {(item) => (
        <Select.Item id={item.id} isDisabled={item.isDisabled}>
          {item.label}
        </Select.Item>
      )}
    </Select>
  );
};

export default AdminSelectRecurringTransaction;
